import {
  Backdrop,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import { useState } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ImageCarousel from "../../components/carousel/ImageCarousel";
import StepperForm from "./StepperForm";
import AcademicHistoryForm from "./AcademicHistoryForm";
import ExamHistoryForm from "./ExamHistoryForm";
import { useRef } from "react";
import StudySelectionForm from "./StudySelectionForm";
import CurrentExamSelection from "./CurrentExamSelection";
import FinanceDetailsForm from "./FinanceDetailsForm";
import { useMemo } from "react";
import CustomSnackbar from "../../../newTestPreo/components/snackbar/Snackbar";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import SubmitLoader from "../../components/Loader/SubmitLoader";

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

const images = [
  "/Signupimages/registration1.png",
  "/Signupimages/registration2.png",
  "/Signupimages/registration3.png",
];

// inside PersonalDetailsForm()

const taglines = [
  "You take the first step—we’ll guide the rest.",
  "Prep smart. Score high. Achieve big.",
  "Smart Prep for Big International Dreams.",
  "Where Global Dreams Meet Smart Prep.",
  "Affordable Paths to Global Education.",
];

function PersonalDetailsForm() {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const [serviceId, setServiceId] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const sId = localStorage.getItem("serviceId");
    const uId = localStorage.getItem("userId");

    if (!sId || !uId) {
      showSnackbar("Missing user or service information", "error");
      return;
    }

    setServiceId(sId);
    setUserId(uId);
  }, []);

  const stepRefs = useMemo(
    () => Array.from({ length: 6 }, () => React.createRef()),
    []
  );

  const steps = useMemo(() => {
    if (!serviceId) return [];

    const baseSteps = [
      {
        title: "Let’s Get to Know You",
        subtitle: "Enter your personal details to start building your journey",
        component: <StepperForm ref={stepRefs[0]} />,
        // component: <AcademicHistoryForm ref={stepRefs[0]} />,
        // component: <ExamHistoryForm ref={stepRefs[0]} />,
        // component: <StudySelectionForm ref={stepRefs[0]} />,
        // component: <FinanceDetailsForm ref={stepRefs[0]} />,
      },
      {
        title: "Showcase Your Academic Journey",
        subtitle: "Highlight the key milestones that shaped your education.",
        component: <AcademicHistoryForm ref={stepRefs[1]} />,
      },
      {
        title: "Highlight Your English Proficiency",
        subtitle:
          "Showcase the milestones that built your language foundation.",
        component: <ExamHistoryForm ref={stepRefs[2]} />,
      },
    ];

    const conditionalSteps =
      serviceId === "1"
        ? [
            {
              title: "Create Your Academic Journey",
              subtitle: "Select academic exams",
              component: <CurrentExamSelection ref={stepRefs[3]} />,
            },
            {
              title: "Create Your Academic Journey",
              subtitle: "Select course type and preferred intake",
              component: <StudySelectionForm ref={stepRefs[4]} />,
            },
          ]
        : serviceId === "2"
        ? [
            {
              title: "Design Your Study Abroad Journey",
              subtitle:
                "Choose your course and intake so we can guide you with precision.",
              component: <StudySelectionForm ref={stepRefs[3]} />,
            },
          ]
        : [];

    const finalStep = [
      {
        title: "Shape your Global Future",
        subtitle: "Set your budget to unlock the right opportunities",
        component: (
          <FinanceDetailsForm
            ref={stepRefs[baseSteps.length + conditionalSteps.length]}
          />
        ),
      },
    ];

    return [...baseSteps, ...conditionalSteps, ...finalStep];
  }, [serviceId, stepRefs]);

  const backgroundImages = useMemo(() => {
    if (!serviceId) return [];

    const images = [];

    // Step 0 → StepperForm
    images.push("/Signupimages/registration1.png");

    // Step 1 → AcademicHistoryForm
    images.push("/Signupimages/registration2.png");

    // Step 2 → ExamHistoryForm
    images.push("/Signupimages/registration4.png");

    // Conditional steps
    if (serviceId === "1") {
      // Step 3 → CurrentExamSelection
      images.push("/Signupimages/registration2.png");

      // Step 4 → StudySelectionForm
      images.push("/Signupimages/registration3.png");
    } else if (serviceId === "2") {
      // Step 3 → StudySelectionForm (direct)
      images.push("/Signupimages/registration3.png");
    }

    // Final step → FinanceDetailsForm
    images.push("/Signupimages/registration5.png");

    return images;
  }, [serviceId]);

  if (!serviceId || steps.length === 0) {
    return <Typography>Loading form...</Typography>;
  }

  // Handle next with per-step validation
  const handleNext = async () => {
    const currentRef = stepRefs[activeStep].current;
    if (currentRef && currentRef.validate()) {
      if (activeStep < steps.length - 1) {
        setActiveStep((prev) => prev + 1);
      } else {
        setSubmitLoading(true);
        const finalData = stepRefs.map((ref) => ref.current?.getData() || {});

        // Merge all step objects into one payload
        const payload = {
          ...Object.assign({}, ...finalData),
          service_id: serviceId,
          user_id: userId,
        };

        try {
          const response = await fetch(
            `${API_ENDPOINT}userdetail/save-user-registration-details`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            }
          );

          if (response.status == 200 || response.ok) {
            setSubmitLoading(false);
            let result = await response.json();
            const token = localStorage.getItem("token");
            const serviceId = localStorage.getItem("serviceId");
            if (token) {
              showSnackbar(
                "✅ Form submitted successfully! Redirecting to dashboard..."
              );
              setTimeout(() => {
                if (serviceId == 2) {
                  navigate("/sap/dashboard");
                } else if (serviceId == 1) {
                  navigate("/test-prep/testprepdashboard");
                } else {
                  localStorage.clear();
                  navigate("/login");
                }
                localStorage.removeItem("serviceId");
              }, 2000);
            } else {
              showSnackbar(
                "✅ Form submitted successfully! Redirecting to login..."
              );
              setTimeout(() => {
                localStorage.clear();
                navigate("/login");
              }, 2000);
            }
          } else {
            setSubmitLoading(false);
            let result = await response.json();
            showSnackbar(`${result.message}`, "error");
          }
        } catch (error) {
          setSubmitLoading(false);
          showSnackbar(
            "⚠️ Could not connect to server. Please check your internet or try again later.",
            "error"
          );
        }
      }
    }
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  return (
    <Grid
      container
      minHeight="100vh"
      sx={{
        backgroundImage: `url(${backgroundImages[activeStep]})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        transition: "background-image 0.6s ease-in-out",
      }}
    >
      {/* LEFT SIDE */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          p: { xs: 2, sm: 3, md: 6 },
          color: "#fff",
          textAlign: { xs: "center", md: "left" },

          // Prevent overflow on small devices
          "@media (max-width:400px)": {
            mt: 2,
          },
        }}
      >
        <Box
          mb={3}
          sx={{
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          <img
            src="/Signupimages/logo.png"
            alt="Logo"
            style={{ width: "90px", height: "auto" }}
          />
        </Box>

        <Box sx={{ mt: "auto", mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              fontFamily: "Crimson Pro",
              fontWeight: 800,
              fontStyle: "normal",
              fontSize: { xs: "24px", sm: "30px", md: "42px" },
              lineHeight: "110%",
              letterSpacing: 0,
              textAlign: { xs: "center", md: "left" },
              color: "#fff",
              textShadow: "0 3px 8px rgba(0,0,0,0.4)",
              px: { xs: 1, md: 0 },
            }}
          >
            {taglines[activeStep]}
          </Typography>
        </Box>
      </Grid>
      {/* Right Side */}
      <Grid
        item
        xs={12}
        md={6}
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={4}
      >
        <Box
          sx={{
            width: "100%",
            background: "linear-gradient(180deg, #FFFCF0 0%, #FFFFFF 100%)",
            borderRadius: 3,
            boxShadow: 3,
            p: 4,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Right top edge img */}
          <Box
            component="img"
            src="/rightTopCircleBg.png"
            alt="rt"
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "110px", // adjust as needed
              height: "auto",
              objectFit: "contain",
            }}
          />
          {/* Center large background image */}
          <Box
            component="img"
            src="/CenterBg.png"
            alt="center-bg"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "350px",
              height: "auto",
              // opacity: 0.15,
              pointerEvents: "none",
              userSelect: "none",
            }}
          />

          {/* Bottom right inset image */}
          <Box
            component="img"
            src="/bottomRightBg.png"
            alt="rb"
            sx={{
              position: "absolute",
              bottom: 16,
              right: 156,
              width: "160px",
              height: "auto",
              objectFit: "contain",
              // opacity: 0.9, // optional
            }}
          />

          <Typography
            fontFamily="Inter"
            fontWeight={600}
            fontSize="22px"
            lineHeight="100%"
            letterSpacing="0px"
            textAlign="start"
            sx={{
              verticalAlign: "middle",
            }}
            mb={2}
            color={"#572575"}
          >
            {steps[activeStep].title}
          </Typography>

          <Typography
            fontFamily="Inter, sans-serif"
            fontWeight={500}
            fontSize="14px"
            lineHeight="100%"
            letterSpacing="0px"
            textAlign="start"
            color="#3F424C"
            sx={{
              verticalAlign: "middle",
            }}
            mb={4}
          >
            {steps[activeStep].subtitle}
          </Typography>

          {/* Scrollable Content */}
          <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
            {steps.map((step, index) => (
              <Box
                key={index}
                sx={{ display: index === activeStep ? "block" : "none" }}
              >
                {step.component}
              </Box>
            ))}
          </Box>

          {/* Navigation Buttons */}
          <Box display="flex" justifyContent="space-between" mt={3}>
            <Button
              variant="outlined"
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{
                borderRadius: 2,
                width: "100px",
                fontFamily: "Inter",
                fontWeight: 400, // Bold
                fontStyle: "normal", // Bold is weight, not style
                fontSize: "medium",
                lineHeight: "100%",
                letterSpacing: "0px",
                verticalAlign: "middle",
                // fontColor: "black",
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                backgroundColor: "#572575",
                "&:hover": { backgroundColor: "#4a0f6a" },
                borderRadius: 2,
                width: "140px",
                height: "40px",
                //  Your text styles
                fontFamily: "Inter",
                fontWeight: 500, // Bold
                fontStyle: "normal", // Bold is weight, not style
                fontSize: "medium",
                lineHeight: "100%",
                letterSpacing: "0px",
                verticalAlign: "middle",
              }}
            >
              {activeStep === steps.length - 1 ? "Submit" : "Continue"}
            </Button>
          </Box>
        </Box>
      </Grid>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
      <SubmitLoader open={submitLoading} text="Saving details..." />
    </Grid>
  );
}

export default PersonalDetailsForm;
